import { SlidersHorizontalIcon, XIcon } from '@phosphor-icons/react'
import { Accordion } from '@radix-ui/react-accordion'
import { DialogClose } from '@radix-ui/react-dialog'
import clsx from 'clsx'
import { useEffect, useMemo, useState } from 'react'
import Button from '@/src/components/core/Button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTrigger,
} from '@/src/components/core/Dialog'
import FilterItem from '@/src/components/flch/components/filter/FilterItem'
import {
  ActiveFilter,
  Facet,
  FilterGroup,
} from '@/src/components/flch/components/filter/types'

const BREAKPOINT_CLASSES = {
  md: {
    sidebar:
      'sticky top-17.5 hidden max-h-[calc(100dvh-4.375rem)] overflow-y-auto overscroll-contain pb-6 md:block',
    mobileBar: 'flex items-center gap-3 md:hidden',
    accordion: 'border-gray-300 md:border-t',
    resetButton:
      'py-3 text-sm font-bold text-gray-800 md:py-0 md:text-gray-600',
  },
  lg: {
    sidebar:
      'sticky top-17.5 hidden max-h-[calc(100dvh-4.375rem)] overflow-y-auto overscroll-contain pb-6 lg:block',
    mobileBar: 'flex items-center gap-3 lg:hidden',
    accordion: 'border-gray-300 lg:border-t',
    resetButton:
      'py-3 text-sm font-bold text-gray-800 lg:py-0 lg:text-gray-600',
  },
} as const

function FacetFilter<T>({
  items,
  facets,
  breakpoint,
  resultLabel,
  onFilteredChange,
}: {
  items: T[]
  facets: Facet<T>[]
  /** Breakpoint above which the sticky sidebar replaces the mobile dialog. */
  breakpoint: keyof typeof BREAKPOINT_CLASSES
  /** Label for the "show N results" button, e.g. `3 Kurse anzeigen`. */
  resultLabel: (count: number) => string
  onFilteredChange: (items: T[]) => void
}) {
  const classes = BREAKPOINT_CLASSES[breakpoint]
  const [selected, setSelected] = useState<Record<string, string[]>>({})

  const facetOptions = useMemo(
    () => facets.map((facet) => facet.options(items)),
    [facets, items]
  )

  const matches = useMemo(
    () => (item: T, skip?: string) =>
      facets.every((facet) => {
        if (facet.name === skip) return true
        const active = selected[facet.name] ?? []
        if (active.length === 0) return true
        const labels = facet.labels(item)
        if (labels.length === 0) return facet.includeUnlabeled ?? false
        return labels.some((label) => active.includes(label))
      }),
    [facets, selected]
  )

  const filteredItems = useMemo(
    () => items.filter((item) => matches(item)),
    [items, matches]
  )

  useEffect(() => {
    onFilteredChange(filteredItems)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredItems])

  const filterGroups: FilterGroup[] = facets
    .map((facet, index) => {
      const options = facetOptions[index]
      const active = selected[facet.name] ?? []
      // Count against items that pass every other facet's filter.
      const countPool = items.filter((item) => matches(item, facet.name))
      const counts: Record<string, number> = {}
      for (const option of options) {
        counts[option] = countPool.filter((item) =>
          facet.labels(item).includes(option)
        ).length
      }

      return {
        name: facet.name,
        options: options.map((name) => ({ name })),
        counts,
        filter: active.map((name) => ({ name })),
        setFilter: (filter: { name: string }[]) =>
          setSelected((prev) => ({
            ...prev,
            [facet.name]: filter.map((f) => f.name),
          })),
      }
    })
    .filter((group) => group.options.length > 1)

  const activeFilters: ActiveFilter[] = filterGroups.flatMap((group) =>
    group.filter.map((option) => ({
      name: option.name,
      onRemove: () =>
        group.setFilter(group.filter.filter((f) => f.name !== option.name)),
    }))
  )

  const resetAll = () => setSelected({})

  return (
    <>
      <div className={classes.sidebar}>
        <p className="text-lg font-black">Filter</p>
        <FilterAccordion
          groups={filterGroups}
          accordionClassName={classes.accordion}
          className="mt-5"
        />
        {activeFilters.length > 0 && (
          <>
            <p className="mt-5 text-sm">Aktive Filter:</p>
            <ActiveFilterTags
              filters={activeFilters}
              className="mt-3 flex-wrap"
            />
            <ResetButton
              onClick={resetAll}
              buttonClassName={classes.resetButton}
              className="mt-4"
            />
          </>
        )}
      </div>
      <div className={classes.mobileBar}>
        <Dialog>
          <DialogTrigger asChild>
            <Button
              version="gray-ghost"
              size={activeFilters.length > 0 ? 'icon' : 'normal'}
              icon={<SlidersHorizontalIcon weight="bold" />}
              iconPosition="right"
              className="shrink-0"
            >
              {activeFilters.length === 0 && 'Filter'}
            </Button>
          </DialogTrigger>
          <DialogContent
            withPositioning={false}
            className="inset-0 max-h-full rounded-none"
          >
            <DialogHeader title="Filter" />
            <DialogBody className="flex-1 pt-3">
              <FilterAccordion
                groups={filterGroups}
                accordionClassName={classes.accordion}
              />
            </DialogBody>
            <div className="flex flex-col gap-3 border-t border-gray-150 px-5 py-4">
              <DialogClose asChild>
                <Button version="blue" className="w-full">
                  {resultLabel(filteredItems.length)}
                </Button>
              </DialogClose>
              <ResetButton
                onClick={resetAll}
                buttonClassName={classes.resetButton}
                className="w-full self-center"
              />
            </div>
          </DialogContent>
        </Dialog>
        {activeFilters.length > 0 && (
          <div className="flex overflow-x-auto border-l border-gray-300">
            <div className="pl-3">
              <ActiveFilterTags
                filters={activeFilters}
                className="flex-nowrap overflow-x-auto"
              />
            </div>
          </div>
        )}
      </div>
    </>
  )
}

const FilterAccordion = ({
  groups,
  accordionClassName,
  className,
}: {
  groups: FilterGroup[]
  accordionClassName: string
  className?: string
}) => (
  <Accordion
    type="multiple"
    defaultValue={groups.map((group) => group.name)}
    className={clsx(accordionClassName, className)}
  >
    {groups.map((group) => (
      <FilterItem
        key={group.name}
        name={group.name}
        options={group.options}
        counts={group.counts}
        filter={group.filter}
        setFilter={group.setFilter}
      />
    ))}
  </Accordion>
)

const ActiveFilterTags = ({
  filters,
  className,
}: {
  filters: ActiveFilter[]
  className?: string
}) => (
  <div className={clsx('flex gap-2', className)}>
    {filters.map((filter) => (
      <FilterTag
        key={filter.name}
        name={filter.name}
        onRemove={filter.onRemove}
      />
    ))}
  </div>
)

const ResetButton = ({
  onClick,
  buttonClassName,
  className,
}: {
  onClick: () => void
  buttonClassName: string
  className?: string
}) => (
  <button onClick={onClick} className={clsx(buttonClassName, className)}>
    Alle Filter zurücksetzen
  </button>
)

const FilterTag = ({
  name,
  onRemove,
}: {
  name: string
  onRemove: () => void
}) => {
  return (
    <button
      onClick={onRemove}
      className="flex shrink-0 items-center rounded-full border border-blue-700 bg-blue-50 py-2 pr-3 pl-4 text-sm"
    >
      {name}
      <XIcon size={16} weight="bold" className="ml-1.5 text-blue-700" />
    </button>
  )
}

export default FacetFilter
